import React, { useContext, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useCurrency } from '@/hooks/useCurrency';
import type { Product, ProductPromotion, PromotionSelectionInput } from '@/types';
import { validateAndResolvePromotion } from '@/services/promotions/promotionValidation';

import { cn } from '@/lib/utils';
import { Minus, Plus } from 'lucide-react';

function buildSelectionInputs(
  definition: ProductPromotion,
  chooseCounts: Record<string, Record<string, number>>,
  sameSelected: Record<string, string>
): PromotionSelectionInput[] {
  const out: PromotionSelectionInput[] = [];
  const sorted = [...definition.groups].sort((a, b) => a.id.localeCompare(b.id));
  for (const g of sorted) {
    if (g.mode === 'choose') {
      const counts = chooseCounts[g.id] ?? {};
      const lines: Array<{ productId: string; quantity: number }> = [];
      for (const opt of g.options) {
        const q = counts[opt.componentProductId] ?? 0;
        if (q > 0) lines.push({ productId: opt.componentProductId, quantity: q });
      }
      out.push({ groupId: g.id, lines });
    } else if (g.mode === 'same_product') {
      const pid = sameSelected[g.id] ?? '';
      out.push({
        groupId: g.id,
        lines: pid ? [{ productId: pid, quantity: g.requiredUnits }] : [],
      });
    }
  }
  return out;
}

function buildInitialSelectionState(definition: ProductPromotion): {
  chooseCounts: Record<string, Record<string, number>>;
  sameSelected: Record<string, string>;
} {
  const chooseCounts: Record<string, Record<string, number>> = {};
  const sameSelected: Record<string, string> = {};
  for (const g of definition.groups) {
    if (g.mode === 'choose') {
      const m: Record<string, number> = {};
      for (const o of g.options) m[o.componentProductId] = 0;
      chooseCounts[g.id] = m;
    }
    if (g.mode === 'same_product') {
      const first = [...g.options].sort((a, b) => a.id.localeCompare(b.id))[0];
      sameSelected[g.id] = first?.componentProductId ?? '';
    }
  }
  return { chooseCounts, sameSelected };
}

interface PromoSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  definition: ProductPromotion | null;
  bundleProduct: Product | null;
  productNameById: Map<string, string>;
  onConfirm: (selections: PromotionSelectionInput[]) => void;
}

type PromoSelectionFormProps = {
  definition: ProductPromotion;
  bundleProduct: Product;
  productNameById: Map<string, string>;
  onConfirm: (selections: PromotionSelectionInput[]) => void;
  onClose: () => void;
};

function PromoSelectionDialogForm({
  definition,
  bundleProduct,
  productNameById,
  onConfirm,
  onClose,
}: PromoSelectionFormProps) {
  const { translator } = useContext(LanguageProviderContext);
  const { formatAmount } = useCurrency();
  const [chooseCounts, setChooseCounts] = useState(() => {
    const initial = buildInitialSelectionState(definition);
    return initial.chooseCounts;
  });
  const [sameSelected, setSameSelected] = useState(() => {
    const initial = buildInitialSelectionState(definition);
    return initial.sameSelected;
  });

  const selectionInputs = useMemo(
    () => buildSelectionInputs(definition, chooseCounts, sameSelected),
    [definition, chooseCounts, sameSelected]
  );

  const isValid = useMemo(() => {
    try {
      validateAndResolvePromotion(definition, selectionInputs);
      return true;
    } catch {
      return false;
    }
  }, [definition, selectionInputs]);

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm(selectionInputs);
    onClose();
  };

  const sortedGroups = [...definition.groups].sort((a, b) => a.id.localeCompare(b.id));

  return (
    <>
      <DialogHeader className="shrink-0 pb-2">
          <DialogTitle className="pr-8">{bundleProduct.name}</DialogTitle>
          <p className="text-sm font-medium text-muted-foreground">{formatAmount(bundleProduct.price)}</p>
          <p className="text-sm text-muted-foreground">
            {translator('Promotions.selectComponentsSubtitle')}
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-2 pl-2 -mx-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 py-2 md:grid-cols-2 md:gap-y-8">
          {sortedGroups.map((g) => (
            <div key={g.id} className="min-w-0 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">
                  {g.label
                    ? g.label
                    : g.mode === 'fixed'
                      ? translator('Promotions.cashierGroupFixed')
                      : g.mode === 'choose'
                        ? translator('Promotions.cashierGroupChoose')
                        : translator('Promotions.cashierGroupSame')}
                </h3>
                {g.mode === 'choose' && (
                  <Badge variant="secondary">
                    {translator('Promotions.chooseProgress', {
                      current: String(
                        Object.values(chooseCounts[g.id] ?? {}).reduce((a, b) => a + b, 0)
                      ),
                      required: String(g.requiredUnits),
                    })}
                  </Badge>
                )}
              </div>

              {g.mode === 'fixed' && (
                <div className="flex flex-wrap gap-2">
                  {[...g.options]
                    .sort((a, b) => a.id.localeCompare(b.id))
                    .filter((o) => o.defaultQuantity > 0)
                    .map((o) => (
                      <Badge key={o.id} variant="outline" className="text-sm">
                        {productNameById.get(o.componentProductId) ?? o.componentProductId} ×{' '}
                        {o.defaultQuantity}
                      </Badge>
                    ))}
                </div>
              )}

              {g.mode === 'choose' && (
                <div className="space-y-2">
                  {[...g.options]
                    .sort((a, b) => a.id.localeCompare(b.id))
                    .map((o) => {
                      const q = chooseCounts[g.id]?.[o.componentProductId] ?? 0;
                      const name = productNameById.get(o.componentProductId) ?? o.componentProductId;
                      const totalSelected = Object.values(chooseCounts[g.id] ?? {}).reduce((a, b) => a + b, 0);
                      const canAdd = totalSelected < g.requiredUnits;
                      return (
                        <div
                          key={o.id}
                          className="flex items-center justify-between gap-2 rounded-md border p-2"
                        >
                          <span className="text-sm font-medium min-w-0 flex-1">
                            {name.length > 40 ? `${name.slice(0, 40)}…` : name}
                          </span>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                setChooseCounts((prev) => ({
                                  ...prev,
                                  [g.id]: {
                                    ...(prev[g.id] ?? {}),
                                    [o.componentProductId]: Math.max(0, q - 1),
                                  },
                                }))
                              }
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-6 text-center text-sm">{q}</span>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              disabled={!canAdd}
                              onClick={() =>
                                setChooseCounts((prev) => ({
                                  ...prev,
                                  [g.id]: {
                                    ...(prev[g.id] ?? {}),
                                    [o.componentProductId]: q + 1,
                                  },
                                }))
                              }
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {g.mode === 'same_product' && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {translator('Promotions.sameProductQtyHint', { qty: String(g.requiredUnits) })}
                  </p>
                  <div className="grid gap-2">
                    {[...g.options]
                      .sort((a, b) => a.id.localeCompare(b.id))
                      .map((o) => {
                        const selected = sameSelected[g.id] === o.componentProductId;
                        return (
                          <Button
                            key={o.id}
                            type="button"
                            variant={selected ? 'default' : 'outline'}
                            className="justify-start h-auto py-2 px-3"
                            onClick={() =>
                              setSameSelected((prev) => ({ ...prev, [g.id]: o.componentProductId }))
                            }
                          >
                            <span className="truncate text-left">
                              {productNameById.get(o.componentProductId) ?? o.componentProductId}
                            </span>
                          </Button>
                        );
                      })}
                  </div>
                </div>
              )}
              <Separator />
            </div>
          ))}
          </div>
        </div>

      <DialogFooter className="shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-4">
        <Button type="button" variant="outline" onClick={onClose}>
          {translator('Global.cancel')}
        </Button>
        <Button type="button" onClick={handleConfirm} disabled={!isValid}>
          {translator('Promotions.addToOrder')}
        </Button>
      </DialogFooter>
    </>
  );
}

export function PromoSelectionDialog({
  open,
  onOpenChange,
  definition,
  bundleProduct,
  productNameById,
  onConfirm,
}: PromoSelectionDialogProps) {

  if (!definition || !bundleProduct) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-none h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] 2xl:max-w-7xl 2xl:h-[85vh] flex flex-col p-4 lg:p-6 overflow-hidden">
        {open ? (
          <PromoSelectionDialogForm
            key={definition.id}
            definition={definition}
            bundleProduct={bundleProduct}
            productNameById={productNameById}
            onConfirm={onConfirm}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
